// src/pages/admin/CourseLectures/lectures/AuPairLecture01Introduction.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${
        isOpen ? "border-[#009fe3]/50" : "border-gray-200"
      } bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-4 text-left transition-colors ${
          isOpen ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${
            isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"
          }`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-base font-bold ${
            isOpen ? "text-[#0369a1]" : "text-gray-800"
          }`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-5 pt-3 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Eligibility Row ───────────────────────────────────────────────
const EligibilityRow: React.FC<{
  criteria: string;
  requirement: string;
  pass: boolean;
}> = ({ criteria, requirement, pass }) => (
  <div className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-3 py-2.5">
    <span
      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
        pass ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
      }`}
    >
      {pass ? "✓" : "✗"}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold text-gray-700">{criteria}</p>
      <p className="text-sm text-gray-500">{requirement}</p>
    </div>
  </div>
);

// ── Pro Con Card ──────────────────────────────────────────────────
const ProConCard: React.FC<{
  icon: string;
  title: string;
  items: string[];
  color: string;
}> = ({ icon, title, items, color }) => (
  <div className={`rounded-xl border p-4 ${color}`}>
    <p className="text-sm font-bold mb-3 flex items-center gap-1.5">
      <span>{icon}</span> {title}
    </p>
    <div className="space-y-2">
      {items.map((item, i) => (
        <div
          key={i}
          className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2"
        >
          <span className="flex-shrink-0 mt-0.5">›</span>
          <p className="text-sm opacity-90">{item}</p>
        </div>
      ))}
    </div>
  </div>
);

// ── Language Level Card ───────────────────────────────────────────
const LevelCard: React.FC<{
  level: string;
  label: string;
  desc: string;
  active?: boolean;
}> = ({ level, label, desc, active }) => (
  <div
    className={`rounded-xl border p-3 text-center ${
      active
        ? "bg-[#009fe3] border-[#009fe3] text-white"
        : "bg-gray-50 border-gray-200 text-gray-500"
    }`}
  >
    <p
      className={`text-xl font-extrabold mb-0.5 ${active ? "text-white" : "text-gray-400"}`}
    >
      {level}
    </p>
    <p
      className={`text-xs font-bold ${active ? "text-white/90" : "text-gray-500"}`}
    >
      {label}
    </p>
    <p className={`text-xs mt-1 ${active ? "text-white/70" : "text-gray-400"}`}>
      {desc}
    </p>
    {active && (
      <span className="inline-block mt-1.5 text-[10px] font-bold bg-white/20 rounded-full px-2 py-0.5">
        MINIMUM REQUIRED
      </span>
    )}
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const AuPairLecture01Introduction: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Course Header Banner */}
      <div className="bg-gradient-to-r from-[#009fe3] to-[#0369a1] rounded-xl px-5 py-5 mb-4">
        <p className="text-xs font-bold uppercase tracking-widest text-white opacity-70 mb-1">
          Au Pair Preparatory Course
        </p>
        <p className="text-xl font-extrabold text-white">
          Session 01 — Introduction & Eligibility
        </p>
        <p className="text-sm text-white opacity-80 mt-1">
          Designed by Guideline2Germany
        </p>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "What is Au Pair? — Overview" },
          { num: "2", label: "Eligibility Check — Do I Qualify?" },
          { num: "3", label: "Advantages — Why Au Pair?" },
          { num: "4", label: "Challenges — Honest Expectations" },
          { num: "5", label: "The Final Goal — What Comes After?" },
          { num: "6", label: "Why German? — Minimum A2 Required" },
        ]}
      />

      {/* 01 — What is Au Pair */}
      <Accordion
        id="01"
        num="SESSION 01"
        icon="👩‍👧"
        title="What is Au Pair? — Overview"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4 mb-4">
          <p className="text-base font-bold text-[#0369a1] mb-2">
            📖 What does "Au Pair" mean?
          </p>
          <p className="text-sm text-[#0369a1]">
            Au Pair comes from French — <strong>"on equal terms"</strong>. You
            live as a family member, not as a paid employee. It is an officially
            recognized cultural exchange program by the German government.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            {
              icon: "🏠",
              label: "Where do you live?",
              desc: "With a German host family — in their home, as part of the household",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              icon: "👶",
              label: "What do you do?",
              desc: "Take care of the children and help with light household tasks",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "💶",
              label: "What do you receive?",
              desc: "Accommodation + meals + ~280 € pocket money/month + language course",
              color: "bg-green-50 border-green-200 text-green-800",
            },
            {
              icon: "📅",
              label: "How long?",
              desc: "Standard: 6–12 months depending on the family and visa",
              color: "bg-amber-50 border-amber-200 text-amber-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl border p-4 ${item.color}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">{item.icon}</span>
                <p className="text-sm font-bold">{item.label}</p>
              </div>
              <p className="text-sm opacity-80 pl-8">{item.desc}</p>
            </div>
          ))}
        </div>

        <InfoBox variant="info" icon="🇩🇪" title="Officially Recognized">
          The Au Pair program is regulated by German law (§ 20 AufenthG). This
          means it is a <strong>safe and legal pathway</strong> to live and work
          in Germany — not a grey area.
        </InfoBox>
      </Accordion>

      {/* 02 — Eligibility Check */}
      <Accordion
        id="02"
        num="SESSION 02"
        icon="✅"
        title="Eligibility Check — Do I Qualify?"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Before anything else — check every single requirement honestly. All
          boxes must be ticked to proceed with the program.
        </p>
        <div className="space-y-2 mb-4">
          <EligibilityRow
            criteria="Age"
            requirement="Must be between 18 and 27 years old"
            pass
          />
          <EligibilityRow
            criteria="Marital Status"
            requirement="Single — no children of your own"
            pass
          />
          <EligibilityRow
            criteria="Education"
            requirement="Completed school education (SSC/HSC minimum)"
            pass
          />
          <EligibilityRow
            criteria="Childcare Experience"
            requirement="Basic experience with children (babysitting, younger siblings, tutoring)"
            pass
          />
          <EligibilityRow
            criteria="German Language"
            requirement="Minimum A2 level before departure — mandatory"
            pass
          />
          <EligibilityRow
            criteria="Nationality"
            requirement="Bangladeshi — national visa (Au Pair) required from German embassy"
            pass
          />
          <EligibilityRow
            criteria="Health"
            requirement="No serious medical conditions — health insurance required"
            pass
          />
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-4">
          <p className="text-base font-bold text-red-800 mb-2">
            ❌ Automatic Disqualification
          </p>
          <div className="space-y-1.5">
            {[
              "Over 27 years old at time of application",
              "Married or has own children",
              "No German language knowledge whatsoever and unwilling to learn",
            ].map((item, i) => (
              <p
                key={i}
                className="text-sm text-red-700 flex items-start gap-1.5"
              >
                <span className="flex-shrink-0 mt-0.5">›</span> {item}
              </p>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 03 — Advantages */}
      <Accordion
        id="03"
        num="SESSION 03"
        icon="🌟"
        title="Advantages — Why Au Pair is a Great Opportunity"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Au Pair is one of the most accessible and legal pathways to Germany
          for young Bangladeshi women. Here is what you genuinely gain:
        </p>
        <div className="space-y-2">
          {[
            {
              icon: "🛂",
              title: "Legal Pathway to Germany",
              desc: "Official residence permit — no grey areas, no risks. Fully government-recognized.",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              icon: "🏠",
              title: "Free Accommodation & Meals",
              desc: "Provided entirely by the host family. No rent, no food expenses.",
              color: "bg-green-50 border-green-200 text-green-800",
            },
            {
              icon: "💶",
              title: "280 € Pocket Money / Month",
              desc: "Fixed by German law — paid by the host family every month, guaranteed.",
              color: "bg-amber-50 border-amber-200 text-amber-800",
            },
            {
              icon: "🎓",
              title: "German Language Course Included",
              desc: "Host family contributes to language school fees. You improve your German while earning.",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "🚀",
              title: "Career Springboard",
              desc: "After Au Pair: Ausbildung, university, or work visa becomes accessible. This is the launchpad.",
              color: "bg-teal-50 border-teal-200 text-teal-800",
            },
            {
              icon: "🌍",
              title: "Real Life in Germany",
              desc: "Not tourism — actual German daily life, culture, and social integration from day one.",
              color: "bg-indigo-50 border-indigo-200 text-indigo-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl border p-4 ${item.color}`}>
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-base font-bold mb-0.5">{item.title}</p>
                  <p className="text-sm opacity-80">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 04 — Challenges */}
      <Accordion
        id="04"
        num="SESSION 04"
        icon="⚠️"
        title="Challenges — Honest Expectations"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4">
          <p className="text-base font-bold text-amber-800 mb-2">
            No sugarcoating — know what you are signing up for.
          </p>
          <p className="text-sm text-amber-700">
            Understanding the challenges now protects you later. Every challenge
            below has a solution — and this course prepares you for all of them.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <ProConCard
            icon="😔"
            title="Emotional Challenges"
            color="bg-red-50 border-red-200 text-red-800"
            items={[
              "Homesickness is real — you are alone in a foreign home",
              "Language barrier feels overwhelming at first",
              "Culture shock — different rules, different social norms",
            ]}
          />
          <ProConCard
            icon="⏰"
            title="Practical Challenges"
            color="bg-orange-50 border-orange-200 text-orange-800"
            items={[
              "No fixed 9-to-5 schedule — hours depend on the family",
              "280 € is pocket money, not a salary — be financially realistic",
              "Fully dependent on the host family for housing",
            ]}
          />
        </div>

        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 mb-4">
          <p className="text-sm font-bold text-purple-800 mb-2">
            🏠 The Host Family Risk
          </p>
          <p className="text-sm text-purple-700">
            Not every host family is good. Some Au Pairs face overwork, lack of
            privacy, or disrespect. This is exactly why{" "}
            <strong>Session 5 (Host Family Selection Strategy)</strong> is one
            of the most important sessions in this course.
          </p>
        </div>

        <InfoBox variant="tip" icon="💪" title="Every challenge has a solution">
          Homesickness → community building. Language barrier → A2 before you
          leave. Bad host family → proper selection process (Session 5). This
          course prepares you for all of it.
        </InfoBox>
      </Accordion>

      {/* 05 — Final Goal */}
      <Accordion
        id="06"
        num="SESSION 05"
        icon="🚀"
        title="The Final Goal — What Comes After Au Pair?"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4 mb-4">
          <p className="text-base font-bold text-[#0369a1] mb-2">
            Au Pair is not the destination — it is the launchpad.
          </p>
          <p className="text-sm text-[#0369a1]">
            Every month in Germany builds language skills, work experience, and
            cultural understanding that opens the next door.
          </p>
        </div>

        <div className="relative pl-6 space-y-3 mb-4">
          <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#009fe3] via-purple-400 to-green-500 rounded-full" />
          {[
            {
              step: "Now",
              label: "Au Pair Program",
              desc: "12 months — German life, childcare, language school, 280 €/month",
              color: "bg-blue-50 border-blue-200 text-blue-800",
              dot: "bg-[#009fe3]",
            },
            {
              step: "After 12 months",
              label: "B1 / B2 German Level",
              desc: "With regular language school attendance — almost every door opens",
              color: "bg-purple-50 border-purple-200 text-purple-800",
              dot: "bg-purple-400",
            },
            {
              step: "Next Step",
              label: "Ausbildung / University / Job",
              desc: "Visa can be converted — Ausbildung, nursing, education, or study program",
              color: "bg-green-50 border-green-200 text-green-800",
              dot: "bg-green-500",
            },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div
                className={`absolute left-0.5 w-3 h-3 rounded-full border-2 border-white ${item.dot}`}
                style={{ top: `${i * 5.5 + 0.5}rem` }}
              />
              <div
                className={`flex-1 rounded-xl border px-4 py-3 ${item.color}`}
              >
                <p className="text-xs font-bold uppercase tracking-wide opacity-60">
                  {item.step}
                </p>
                <p className="text-base font-bold">{item.label}</p>
                <p className="text-sm opacity-80 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-bold text-green-800 mb-3">
            Real outcomes from former Au Pairs:
          </p>
          <div className="space-y-2">
            {[
              "Working as a Pflegefachfrau (nursing professional) in Germany",
              "Completed Ausbildung as Erzieherin (educator) after Au Pair",
              "Enrolled in a German university with a scholarship",
              "Extended visa to a work permit after gaining B2 level",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2"
              >
                <span className="text-green-500 font-bold flex-shrink-0 mt-0.5">
                  ✓
                </span>
                <p className="text-sm text-green-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 06 — Why German */}
      <Accordion
        id="07"
        num="SESSION 06"
        icon="🇩🇪"
        title="Why German? — Minimum A2 Required"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4">
          <p className="text-base font-bold text-red-800 mb-2">
            German is not optional — it is the foundation of everything.
          </p>
          <p className="text-sm text-red-700">
            Without A2 German before departure, the visa interview, the host
            family communication, and daily life in Germany become serious
            obstacles.
          </p>
        </div>

        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
          Why A2 is the minimum — 4 real reasons:
        </p>
        <div className="space-y-2 mb-5">
          {[
            {
              icon: "🏛️",
              title: "The German Embassy checks it",
              desc: "Basic German language skills are tested or expected during the visa interview. No language = high risk of rejection.",
              color: "bg-red-50 border-red-200 text-red-800",
            },
            {
              icon: "👨‍👩‍👧",
              title: "Host families expect it",
              desc: "From day one, you communicate with the family in German. They will not speak Bengali or English to you.",
              color: "bg-orange-50 border-orange-200 text-orange-800",
            },
            {
              icon: "👶",
              title: "The children speak only German",
              desc: "You play, explain, comfort and care for children entirely in German. This is your daily reality.",
              color: "bg-amber-50 border-amber-200 text-amber-800",
            },
            {
              icon: "🎓",
              title: "Language school placement starts at A2",
              desc: "Most German language schools place new arrivals into A2 or B1. Coming with A1 or zero puts you behind from the start.",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl border p-4 ${item.color}`}>
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-base font-bold mb-0.5">{item.title}</p>
                  <p className="text-sm opacity-80">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
          Language Levels — Where do you need to be?
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
          {[
            { level: "A1", label: "Beginner", desc: "Greetings only" },
            {
              level: "A2",
              label: "Elementary",
              desc: "Daily basics",
              active: true,
            },
            { level: "B1", label: "Intermediate", desc: "Conversations" },
            { level: "B2", label: "Upper-Int.", desc: "Fluent enough" },
            { level: "C1", label: "Advanced", desc: "Professional" },
            { level: "C2", label: "Mastery", desc: "Native-like" },
          ].map((l) => (
            <LevelCard
              key={l.level}
              level={l.level}
              label={l.label}
              desc={l.desc}
              active={l.active}
            />
          ))}
        </div>

        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-base font-bold text-green-800 mb-3">
            📍 Where to learn German in Bangladesh
          </p>
          <div className="space-y-2">
            {[
              {
                name: "Goethe-Institut Dhaka",
                desc: "Most recognized certificate — accepted by German embassy. Best choice.",
                badge: "Most Recognized",
                badgeColor: "bg-[#009fe3] text-white",
              },
              {
                name: "DAAD Language Programs",
                desc: "German Academic Exchange Service — reliable and structured.",
                badge: "Recommended",
                badgeColor: "bg-green-700 text-white",
              },
              {
                name: "Deutsche Welle (dw.com/learn-german)",
                desc: "Free online German learning — great supplement, not a replacement.",
                badge: "Free",
                badgeColor: "bg-amber-500 text-white",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-green-200 px-4 py-3"
              >
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-sm font-bold text-green-800">
                    {item.name}
                  </p>
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.badgeColor}`}
                  >
                    {item.badge}
                  </span>
                </div>
                <p className="text-sm text-green-700 opacity-80">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* Summary */}
      <Accordion
        id="fazit"
        num="SUMMARY"
        icon="🏁"
        title="Session Summary"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-5 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-base font-bold text-green-800 mb-3">
              You now understand the full foundation of the Au Pair program.
            </p>
            <ul className="space-y-1.5">
              {[
                "Au Pair = legal, official cultural exchange — not a grey area",
                "Eligibility: 18–27, single, no children, A2 German minimum",
                "Advantages: free housing, 280 €/month, language course, career springboard",
                "Challenges are real — homesickness, dependency, culture shock",
                "10 sessions ahead — each builds on the previous",
                "Final goal: Ausbildung, university, or work visa after Au Pair",
                "German A2 is not optional — it is the foundation of everything",
                "Start learning at Goethe-Institut Dhaka immediately",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Tasks */}
      <Accordion
        id="aufgaben"
        num="TASKS"
        icon="📋"
        title="Your Tasks — Session 01"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>Eligibility Self-Check</strong> — Go through every
                requirement and honestly confirm you qualify
              </>,
              <>
                <strong>Assess your German level</strong> — Are you A1, A2, or a
                complete beginner? Be honest.
              </>,
              <>
                <strong>Research Goethe-Institut Dhaka</strong> — Find the next
                available A2 course, its schedule and cost
              </>,
              <>
                Memorize this sentence:{" "}
                <strong>"Ich möchte Au Pair in Deutschland werden."</strong> —
                this is your first German sentence
              </>,
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default AuPairLecture01Introduction;
